/**
 * Data and effect layer for the site dashboard.
 *
 * Owns the [siteId] bootstrap effect (room join/leave, reference/capabilities
 * loads, customer entity fetch) and the legacy Immutable entity-seeder effect.
 * Server-derived data comes from the normalized per-entity dashboard stores,
 * seeded by the composite site snapshot fetch (useRetrieveSiteQuery) and read
 * via selectX(siteId) selectors; the primary site group (for the feature-gated
 * curfew) comes via useRetrieveSiteGroupQuery — the cascade reuses any cache
 * entry already in state. The view calls this one hook for all of it.
 *
 * The [siteId] bootstrap installs its targeted room-confirmation listener
 * before joining and removes that listener before leaving.
 *
 * Contains:
 * - useSiteDashboardData — hook (default export)
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCustomer } from '@sophocles/entities/Customer';
import { fetchSite, seedSiteContext } from '@sophocles/entities/Site';
import { receiveSiteGroups } from '@sophocles/entities/SiteGroup';
import { setCustomer } from '@sophocles/features/auth/authActions';
import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { selectCapabilities, loadCapabilities } from '@sophocles/features/capabilities';
import {
  useRetrieveSiteQuery,
  selectSiteAlarms,
  selectSiteCommandActivity,
  selectSiteData,
  selectSitePermissions,
  selectSiteUnit,
  selectSiteUpgradeBusy,
  selectSiteUpgradeStartPending,
  selectSiteWeather,
  shouldPollCommandActivity,
} from '@sophocles/features/entities/site';
import { useRetrieveSiteGroupQuery } from '@sophocles/features/entities/siteGroups';
import { loadCustomerFeatureAssociations } from '@sophocles/features/featureFlags/api';
import { loadReference, selectAlarmDefinitions } from '@sophocles/features/reference';
import { setEntityData } from '@sophocles/features/resources/resourceHandlerActions';
import { setPlant } from '@sophocles/features/sites/slices/selectedPlant/actions';
import { setStructure } from '@sophocles/features/sites/slices/selectedStructure/actions';
import { getSelectedStructure } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { isSiteRoomJoinConfirmation } from '@sophocles/socket-io/roomNames';
import { joinSiteRoomV2, leaveSiteRoomV2, siteSocket } from '@sophocles/socket-io/slice';
import { resolveDerivedSiteGroupId } from '@sophocles/views/sites/hooks/dashboardSiteGroup';

// The OTA-start lease (markUpgradeStarting) is released only by a unit
// snapshot; if the upgrade-start `unit` emit is lost, no later socket event
// releases it. One delayed snapshot read bounds that window.
const UPGRADE_START_RECONCILE_MS = 15000;

/**
 * Fetches and streams all server-derived data needed by the site dashboard.
 *
 * @param {string} siteId   the site id from the route params
 * @param {Object} params   route params (cust_id, site_id, and sitegroup_id where
 *   the route names a group; without one the site's primary group is derived
 *   from the snapshot)
 * @returns {Object}        server data + selectors the view needs, plus refetch
 */
export default function useSiteDashboardData(siteId, params) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [roomJoinRevision, setRoomJoinRevision] = useState(0);
  const handledRoomJoinRevisionRef = useRef(0);

  // ── Auth / customer context ────────────────────────────────────────────────
  // TODO(odl-app-contract): customer entity (preferences.site_schema) still read
  // from legacy getSelectedCustomerSelector. is_billboard is now on the site
  // snapshot (site.is_billboard) and read directly by consumers; customer here
  // is kept for preferences and for ThirdPartiesTab's siteId=null sharing path.
  const customer = useSelector(getSelectedCustomerSelector);
  // Legacy Immutable site entity — seeded by the nav-seeder effect below.
  // Used only for passing to legacy dialogs that call site.get('id') or site.toJS().
  const selectedStructure = useSelector(getSelectedStructure);

  // ── Composite snapshot subscription ────────────────────────────────────────
  // Mounting this subscription drives the fetch and de-dupes a re-mount with the
  // same siteId (no skeleton flash). onQueryStarted fans the response out into
  // the dashboard stores; the socket events (`unit`, `alarms`,
  // `command_activity`, `upgrade_progress`) keep those stores live between
  // commands. While command activity is non-idle (or an upgrade is busy) the
  // snapshot polls: only a REST snapshot retires the accepted-dispatch lease,
  // so the poll is what releases the command lock without a manual refresh.
  // refetch() backs pull-to-refresh and error retry.
  const commandActivity = useSelector(selectSiteCommandActivity(siteId));
  const unit = useSelector(selectSiteUnit(siteId));
  const upgradeBusy = useSelector(selectSiteUpgradeBusy(siteId));
  const pollDashboardState = shouldPollCommandActivity(commandActivity) || upgradeBusy;
  const {
    isFetching,
    isLoading,
    error: queryError,
    refetch,
  } = useRetrieveSiteQuery(
    { id: siteId },
    {
      skip: siteId == null,
      pollingInterval: pollDashboardState ? 5000 : 0,
    },
  );

  // Server data is read from the normalized per-entity dashboard stores (seeded
  // by the fetch above via ingestSiteSnapshot, kept live by the socket events),
  // not the query cache directly. The query above only drives the fetch and the
  // loading/error/refetch surface.
  const site = useSelector(selectSiteData(siteId));
  const alarms = useSelector(selectSiteAlarms(siteId));
  const weather = useSelector(selectSiteWeather(siteId));
  const sitePermissions = useSelector(selectSitePermissions(siteId));
  const loading = isLoading;
  const dashboardError = queryError
    ? (queryError?.data?.detail ?? t('sites.failedToLoadDashboard', 'Failed to load dashboard'))
    : null;

  // ── Curfew (site-group property, via the hierarchy cascade) ────────────────
  // Curfew belongs to the primary site group, not the site, so it is read from
  // the site-group entity (site-groups/{id}/ carries a feature-gated curfew).
  // Skipped until the snapshot resolves the primary group (site.owner.id); the
  // query reuses the cache entry if the group is already loaded.
  const primarySiteGroupId = site?.owner?.id;
  const { data: siteGroup } = useRetrieveSiteGroupQuery(
    { id: primarySiteGroupId },
    { skip: primarySiteGroupId == null },
  );
  const curfew = siteGroup?.curfew ?? null;

  // User-scoped capabilities (once per session).
  const capabilities = useSelector(selectCapabilities);

  // Alarm definitions from the reference catalog.
  const alarmDefs = useSelector(selectAlarmDefinitions);

  // ── Effects ────────────────────────────────────────────────────────────────

  // Re-run whenever siteId changes (same route pattern, different site). Leaves
  // the prior site room before joining the new one so realtime deltas never
  // patch the wrong snapshot. The targeted room confirmation triggers one
  // subscribe-then-snapshot refetch, closing the gap between the render-time
  // query and the effect-time room join. The listener remains active so every
  // reconnect/rejoin gets the same authoritative catch-up read.
  useEffect(() => {
    const customerId = params.cust_id;
    const siteGroupId = params.sitegroup_id;
    const handleSiteRoomJoined = confirmation => {
      if (isSiteRoomJoinConfirmation(confirmation, siteId)) {
        setRoomJoinRevision(revision => revision + 1);
      }
    };

    dispatch(setCustomer(customerId));
    dispatch(setStructure(siteId));
    // Only a route that names the group can select one this early. Where it
    // doesn't, the group is derived from the snapshot below instead of being
    // cleared here — clearing it would leave a site-to-site switch inside one
    // group with no plant selected at all.
    if (siteGroupId != null) dispatch(setPlant(siteGroupId));
    dispatch(setEntityData('Structure', { id: siteId }));
    siteSocket.on('room_joined', handleSiteRoomJoined);
    dispatch(joinSiteRoomV2(siteId));

    // Hydrate the legacy Immutable structure (incl. its full relay_controller
    // join) once at the layout. Nested subpages that still read
    // selectedStructure.get('relay_controller') via the legacy relay-display
    // utils (power/readings, charting cards) depend on this; they used to
    // trigger it themselves through useSite() before those views were flattened
    // onto the layout context.
    dispatch(fetchSite(siteId));

    // Customer entity (5-min client cache).
    dispatch(fetchCustomer(customerId));

    // Customer feature associations gate per-customer view branches (e.g.
    // ReadingsRouterView's NEW_READINGS_VIEW). This layout is a flat sibling
    // of CustomerLayout/SiteGroupLayout, so a hard refresh to a site URL
    // never mounts the layouts that otherwise load them (useCustomer).
    dispatch(loadCustomerFeatureAssociations(customerId));

    // Reference catalogs and capabilities load once per session.
    dispatch(loadReference());
    dispatch(loadCapabilities());

    return () => {
      siteSocket.off('room_joined', handleSiteRoomJoined);
      dispatch(leaveSiteRoomV2(siteId));
    };
  }, [siteId]);

  // A refetch requested while the initial query is still pending can de-duplicate
  // onto that pre-subscription request. Wait for it to settle, then start exactly
  // one request whose read boundary is after the latest confirmed room join.
  useEffect(() => {
    if (roomJoinRevision === 0 || isFetching || handledRoomJoinRevisionRef.current === roomJoinRevision) {
      return;
    }
    handledRoomJoinRevisionRef.current = roomJoinRevision;
    refetch();
  }, [isFetching, refetch, roomJoinRevision]);

  // While an accepted OTA start is still awaiting its `unit` snapshot, arm a
  // single reconciliation read. The upgrade-start socket event normally lands
  // first and cancels the timer; when it doesn't, the read's request version
  // retires the lease so the site's command surfaces cannot stay locked.
  const upgradeStartPending = useSelector(selectSiteUpgradeStartPending(siteId));
  useEffect(() => {
    if (!upgradeStartPending) return undefined;
    const timer = setTimeout(refetch, UPGRADE_START_RECONCILE_MS);
    return () => clearTimeout(timer);
  }, [upgradeStartPending, refetch]);

  // Seed the legacy Immutable entity stores from the new snapshot so the LeftNav
  // can render site/siteGroup context (name, site_groups) without fetchSite/
  // fetchSiteGroup. Runs once the snapshot's site object is available.
  useEffect(() => {
    if (!site) return;
    // Build the site_groups array from owner + shared_with so LeftNav's
    // showStructure check (site_groups.indexOf(plantId)) passes.
    const siteGroupIds = [site.owner?.id, ...(site.shared_with?.map(sg => sg.id) ?? [])].filter(Boolean);
    // seedSiteContext merges into any record fetchSite already stored;
    // seeding via receiveSites would replace it wholesale and null
    // relay_controller — the fetch key the power/readings subpages gate on.
    dispatch(
      seedSiteContext(siteId, {
        id: Number(siteId),
        name: site.name ?? '',
        description: site.description ?? '',
        location_description: site.location_description ?? '',
        time_zone: site.time_zone ?? 'UTC',
        latitude: site.latitude ?? 0,
        longitude: site.longitude ?? 0,
        site_groups: siteGroupIds,
      }),
    );
    // Seed every membership (owner + shared_with), not just the primary
    // group — a deep link can land on a non-owner group id
    // (params.sitegroup_id), and getSelectedPlantSelector looks that id up
    // in this same store. Owner-only seeding left non-owner lookups
    // permanently empty (e.g. PopGrid's Excel export falling back to its
    // "no plant" labels for a shared-group deep link).
    // is_virtual comes straight off each membership's own ref (the site
    // contract's owner/shared_with carry it per-group) — this is the only
    // place a hard refresh on a site route re-hydrates the legacy SiteGroup
    // store, since SiteGroupLayout/CustomerLayout never mount on this flat
    // route; a wrong value here misrenders every is_virtual-gated affordance
    // (StructureCard's Cloud chip, DeactivateSiteDialog, MoveSiteDialog).
    const memberships = [site.owner, ...(site.shared_with ?? [])].filter(Boolean);
    if (memberships.length > 0) {
      dispatch(
        receiveSiteGroups(
          Object.fromEntries(
            memberships.map(sg => [
              sg.id,
              {
                id: sg.id,
                name: sg.name ?? '',
                description: sg.description ?? '',
                customer: sg.customer?.id ?? null,
                is_virtual: sg.is_virtual ?? false,
              },
            ]),
          ),
        ),
      );
    }
  }, [site, siteId, params.sitegroup_id, dispatch]);

  // Callers that reach a site without a group in their route (the customer
  // map's site detail panel) select the site's primary group once the snapshot
  // names it, so everything reading the legacy selected-plant slice — the
  // relay calendar jump, StructureCard's shared-site affordances, the legacy
  // alarm/history dialogs — sees the same group the dashboard route would have
  // named. Keyed on siteId as well as the resolved id, so moving between two
  // sites in one group re-selects it.
  const derivedSiteGroupId = resolveDerivedSiteGroupId(params.sitegroup_id, site);
  useEffect(() => {
    if (derivedSiteGroupId == null) return;
    dispatch(setPlant(derivedSiteGroupId));
  }, [dispatch, siteId, derivedSiteGroupId]);

  return {
    // Redux / server data
    customer,
    selectedStructure,
    loading,
    dashboardError,
    unit,
    site,
    alarms,
    weather,
    capabilities,
    sitePermissions,
    curfew,
    alarmDefs,
    // Pull-to-refresh / error retry (replaces the old loadSiteDashboard force).
    refetch,
  };
}
